import React from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { CalendarIcon, Filter, X } from 'lucide-react';

interface ReportFiltersProps {
  startDate: Date;
  endDate: Date;
  onStartDateChange: (date: Date) => void;
  onEndDateChange: (date: Date) => void;
  ticketTypes: string[];
  onTicketTypesChange: (types: string[]) => void;
  priority: string;
  onPriorityChange: (priority: string) => void;
}

const TICKET_TYPE_OPTIONS = [
  { value: 'incidente', label: 'Incidentes' },
  { value: 'requisicao', label: 'Requisições' },
  { value: 'problema', label: 'Problemas' },
  { value: 'mudanca', label: 'Mudanças' },
];

const PRIORITY_OPTIONS = [
  { value: 'all', label: 'Todas' },
  { value: 'critica', label: 'Crítica' },
  { value: 'alta', label: 'Alta' },
  { value: 'media', label: 'Média' },
  { value: 'baixa', label: 'Baixa' },
];

export const ReportFilters = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  ticketTypes,
  onTicketTypesChange,
  priority,
  onPriorityChange,
}: ReportFiltersProps) => {
  const toggleTicketType = (type: string) => {
    if (ticketTypes.includes(type)) {
      onTicketTypesChange(ticketTypes.filter(t => t !== type));
    } else {
      onTicketTypesChange([...ticketTypes, type]);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-3 p-4 bg-muted/30 rounded-lg border">
      <Filter className="h-4 w-4 text-muted-foreground" />
      
      {/* Date Range */}
      <div className="flex items-center gap-2">
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs">
              <CalendarIcon className="h-3.5 w-3.5" />
              {format(startDate, "dd/MM/yyyy", { locale: ptBR })}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={startDate}
              onSelect={(date) => date && onStartDateChange(date)}
              initialFocus
              className={cn("p-3 pointer-events-auto")}
            />
          </PopoverContent>
        </Popover>
        <span className="text-xs text-muted-foreground">até</span>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs">
              <CalendarIcon className="h-3.5 w-3.5" />
              {format(endDate, "dd/MM/yyyy", { locale: ptBR })}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={endDate}
              onSelect={(date) => date && onEndDateChange(date)}
              initialFocus
              className={cn("p-3 pointer-events-auto")}
            />
          </PopoverContent>
        </Popover>
      </div>

      <div className="h-6 w-px bg-border" />

      {/* Ticket Types */}
      <div className="flex items-center gap-1.5">
        {TICKET_TYPE_OPTIONS.map(opt => (
          <Badge
            key={opt.value}
            variant={ticketTypes.includes(opt.value) ? 'default' : 'outline'}
            className="cursor-pointer text-xs h-7 transition-colors"
            onClick={() => toggleTicketType(opt.value)}
          >
            {opt.label}
            {ticketTypes.includes(opt.value) && (
              <X className="h-3 w-3 ml-1" />
            )}
          </Badge>
        ))}
      </div>

      <div className="h-6 w-px bg-border" />

      {/* Priority */}
      <Select value={priority} onValueChange={onPriorityChange}>
        <SelectTrigger className="h-8 w-[130px] text-xs">
          <SelectValue placeholder="Prioridade" />
        </SelectTrigger>
        <SelectContent>
          {PRIORITY_OPTIONS.map(opt => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};
